import '../css/CloneReportModal.less';

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import _ from 'lodash';
import {Button, Modal} from 'semantic-ui-react';
import {useUserProjectsQuery} from '../generated/graphql';
import {useViewer} from '../state/viewer/hooks';
import {
  DropdownOption,
  getProjectsFromUserProjectsQuery,
  getTeamsFromUserProjectsQuery,
} from './SaveSnapshotModal';
import ModifiedDropdown from './elements/ModifiedDropdown';
import * as ViewApi from '../state/views/api';
import {ID} from '../util/string';
import * as ViewNormalize from '../state/views/normalize';
import * as Urls from '../util/urls';
import {useApolloClient, useSelector} from '../state/hooks';
import * as ViewSelectors from '../state/views/selectors';
import {ReportViewRef} from '../state/reports/types';
import {SaveableView} from '../state/views/types';
import {ReportConfig} from '../state/views/report/types';
import makeComp from '../util/profiler';

interface CloneReportModalProps {
  viewRef: ReportViewRef;
  onClose(): void;
}

const CloneReportModal: React.FC<CloneReportModalProps> = makeComp(
  ({viewRef, onClose}) => {
    const client = useApolloClient();
    const viewer = useViewer();
    const views = useSelector(ViewSelectors.getViews);

    const userProjectsQuery = useUserProjectsQuery({
      variables: {userName: viewer?.username ?? '', includeReports: false},
      skip: viewer == null,
    });

    const view = useSelector(state => state.views.views[viewRef.id]);
    const {id: reportID, entityName, name: projectName} = view.project;
    if (reportID == null) {
      throw new Error('invalid state');
    }
    const viewPartRef = view.partRef;
    if (viewPartRef == null) {
      throw new Error('View in redux missing part ref');
    }

    const projectOptions: DropdownOption[] = useMemo(() => {
      if (userProjectsQuery.loading) {
        return [];
      }
      const teams = getTeamsFromUserProjectsQuery(userProjectsQuery);
      const opts: DropdownOption[] = [];
      for (const t of teams) {
        const teamOpts =
          t?.projects?.edges.map(e => ({
            text:
              teams.length > 1
                ? `${e.node?.entityName}/${e.node?.name}`
                : e.node?.name ?? '',
            value: e.node?.id ?? '',
          })) ?? [];
        opts.push(...teamOpts);
      }
      return opts;
    }, [userProjectsQuery]);

    const [selectedProjectID, setSelectedProjectID] = useState('');
    const selectedProject = useMemo(() => {
      if (userProjectsQuery.loading) {
        return null;
      }
      const projects = getProjectsFromUserProjectsQuery(userProjectsQuery);
      return projects.find(p => p.id === selectedProjectID);
    }, [selectedProjectID, userProjectsQuery]);

    const onChangeProject = useCallback((e, data) => {
      setSelectedProjectID(data.value);
    }, []);

    useEffect(() => {
      const projects = getProjectsFromUserProjectsQuery(userProjectsQuery);
      if (projects.length === 0) {
        return;
      }
      // if a project on the list is already selected, don't change it against the user's will
      let projo = projects.find(p => p.id === selectedProjectID);
      // otherwise, select current project
      if (projo == null) {
        projo = projects.find(
          p => p.entityName === entityName && p.name === projectName
        );
      }
      // if current project is not on the list, default to first project
      if (projo == null) {
        projo = projects[0];
      }
      setSelectedProjectID(projo.id);
      // eslint-disable-next-line
    }, [userProjectsQuery, entityName, projectName]);

    const [cloningReport, setCloningReport] = useState(false);

    const cloneReport = useCallback(async () => {
      if (selectedProject == null || cloningReport) {
        return;
      }

      const spec = ViewNormalize.denormalize(
        views.parts,
        viewPartRef
      ) as ReportConfig;

      setCloningReport(true);
      const project = _.pick(
        selectedProject,
        'entityName',
        'name'
      ) as NonNullable<SaveableView['project']>;

      const newDraft = await ViewApi.save(client, {
        type: 'runs/draft',
        name: ID(12),
        displayName: `Copy of ${view.user.username}'s ${view.displayName}`,
        description: view.description,
        spec,
        project,
        previewUrl: view.previewUrl,
        coverUrl: view.coverUrl,
      });

      window.location.href = Urls.reportEdit({
        entityName: project.entityName,
        projectName: project.name,
        reportID: newDraft.id as string,
        reportName: newDraft.displayName,
      });
    }, [cloningReport, client, view, views, viewPartRef, selectedProject]);

    return (
      <Modal className="clone-report-modal" open onClose={onClose}>
        <Modal.Header>Clone Report</Modal.Header>
        <Modal.Content>
          <p className="clone-report-modal__text">
            Pick a destination for your cloned report.
          </p>
          <ModifiedDropdown
            selection
            search
            className="clone-report-modal__select"
            options={projectOptions}
            value={selectedProjectID}
            onChange={onChangeProject}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button disabled={cloningReport} onClick={onClose}>
            Back
          </Button>
          <Button
            primary
            loading={cloningReport}
            disabled={cloningReport}
            onClick={cloneReport}>
            Clone report
          </Button>
        </Modal.Actions>
      </Modal>
    );
  },
  {id: 'CloneReportModal', memo: true}
);

export default CloneReportModal;
